import { Container, Graphics } from 'pixi.js';

/**
 * Confetti burst — short-lived particle shower fired on wheel-
 * land. ~50 particles, each with a random colour + trajectory
 * that arcs outward then falls under gravity. Producer brief
 * (legacy WheelResolveView): match the CSS confetti recipe so the
 * landing reads with the same "win" beat.
 *
 * <p>Particles are pooled per-burst — the widget creates them on
 * {@link burst} and destroys them when the tween completes. No
 * standing allocation between bursts.
 */

const COLORS: ReadonlyArray<number> = [
  0xffd66b, 0xff6bb5, 0x6bcbff, 0x9de54e, 0xb984ff,
];

interface ConfettiParticle {
  gfx: Graphics;
  peakDx: number;
  peakDy: number;
  finalDx: number;
  finalDy: number;
  rot: number;
  durationMs: number;
  delayMs: number;
  elapsedMs: number;
  size: number;
  ratio: number;
}

export class WheelResolveConfetti extends Container {
  private active: ConfettiParticle[] = [];

  constructor() {
    super();
    this.visible = false;
  }

  /** Fire a fresh burst of {@code count} particles. The widget
   *  schedules itself off the host scene's ticker via {@link tick}. */
  burst(count = 50): void {
    this.clearActive();
    this.visible = true;
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const upBias = -50 - Math.random() * 90;
      const sideBias = (Math.random() - 0.5) * 240;
      const dx = Math.cos(angle) * 60 + sideBias;
      const dy = Math.sin(angle) * 60 + upBias;
      const finalDx = dx + (Math.random() - 0.5) * 80;
      const finalDy = dy + 360 + Math.random() * 120;
      const size = 5 + Math.random() * 6;
      const ratio = 0.4 + Math.random() * 0.5;
      const color = COLORS[i % COLORS.length]!;
      const gfx = new Graphics();
      gfx
        .rect(-size / 2, -(size * ratio) / 2, size, size * ratio)
        .fill({ color });
      gfx.alpha = 0;
      this.addChild(gfx);
      this.active.push({
        gfx,
        peakDx: dx,
        peakDy: dy,
        finalDx,
        finalDy,
        rot: (Math.random() - 0.5) * 1080,
        durationMs: 1800 + Math.random() * 600,
        delayMs: Math.random() * 200,
        elapsedMs: 0,
        size,
        ratio,
      });
    }
  }

  tick(deltaMs: number): void {
    if (!this.visible || this.active.length === 0) return;
    let allDone = true;
    for (const p of this.active) {
      p.elapsedMs += deltaMs;
      const t = (p.elapsedMs - p.delayMs) / p.durationMs;
      if (t < 0) {
        p.gfx.alpha = 0;
        allDone = false;
        continue;
      }
      if (t >= 1) {
        p.gfx.alpha = 0;
        continue;
      }
      allDone = false;
      // Trajectory: peak at t=0.5, final at t=1.
      const x = lerpEase(0, p.peakDx, p.finalDx, t);
      const y = lerpEase(0, p.peakDy, p.finalDy, t);
      p.gfx.x = x;
      p.gfx.y = y;
      p.gfx.angle = p.rot * t;
      if (t < 0.12) p.gfx.alpha = t / 0.12;
      else if (t > 0.85) p.gfx.alpha = (1 - t) / 0.15;
      else p.gfx.alpha = 1;
    }
    if (allDone) this.clearActive();
  }

  private clearActive(): void {
    for (const p of this.active) p.gfx.destroy();
    this.active = [];
    this.visible = false;
  }

  destroyDisplay(): void {
    this.clearActive();
  }
}

function lerpEase(
  start: number,
  peak: number,
  end: number,
  t: number,
): number {
  // Quadratic bezier through (0,start) → (0.5,peak) → (1,end).
  const a = start + (peak - start) * 2 * t;
  const b = peak + (end - peak) * (2 * t - 1);
  return t < 0.5 ? a : b;
}
